import { Box, makeStyles } from '@material-ui/core';
import {
  QuoteProductDTO,
  QuoteItemDTO
} from '@setvi/shared/services/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { SEmptyState } from '@setvi/shared/components';
import { useQuoteData, useQuoteItemsData } from '../../context';
import { useEnrichVisibleItems } from '../../hooks/use-enrich-visible-items';
import { QuoteRowPresenceProvider } from '../../pages/details/realtime/quote-row-presence';
import { useQuotePresenceStore } from '../../pages/details/realtime/store';
import { QuoteFieldLockProvider } from '../../pages/details/realtime/use-field-locks';
import { ProductsTableHeader } from './components/table-header';
import { ProductRow } from './components/product-row';
import { SectionRow } from './section-row';

const OUTER_GAP = 12;

const useStyles = makeStyles(() => ({
  container: {
    overflow: 'auto',
    position: 'relative',
    flex: 1,
    paddingBottom: 100
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: '#F8F9FA'
  }
}));

interface ProductsTableProps {
  onProductClick?: (product: QuoteProductDTO) => void;
}

const itemIdEquals = (item: QuoteItemDTO | QuoteProductDTO, id: string) =>
  (item?.id || item?._id) === id;

/**
 * Resolves a (possibly nested) quote item id to its TOP-LEVEL row index in the
 * virtualized list, so following a collaborator who is on an option or a
 * section child still scrolls to the right row. Matches the item itself, a
 * direct child (section product / product option), or a grandchild (option of a
 * product inside a section).
 */
const findTopLevelIndex = (items: QuoteItemDTO[], targetId: string) =>
  items.findIndex(item => {
    if (itemIdEquals(item, targetId)) return true;
    const children = ('children' in item && item.children) || [];
    return (children as QuoteProductDTO[]).some(
      child =>
        itemIdEquals(child, targetId) ||
        (child.children as QuoteProductDTO[] | undefined)?.some(grandChild =>
          itemIdEquals(grandChild, targetId)
        )
    );
  });

/**
 * Products Table - Display and manage quote items
 * Simplified structure following v1 pattern with separate row components
 */
export const ProductsTable = ({ onProductClick }: ProductsTableProps) => {
  const classes = useStyles();
  const { quoteId, quote } = useQuoteData();
  const isRfqQuote = quote?.type === 'rfq';
  const { items: quoteItems } = useQuoteItemsData();
  const [searchParams, setSearchParams] = useSearchParams();
  const scrollToItemId = useQuotePresenceStore(s => s.scrollToItemId);
  const requestScrollToItem = useQuotePresenceStore(s => s.requestScrollToItem);
  const setHighlightedItemId = useQuotePresenceStore(
    s => s.setHighlightedItemId
  );
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Get search query from URL
  const searchQuery = searchParams.get('search') || '';

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery) return quoteItems || [];

    const query = searchQuery.toLowerCase();
    const filtered: QuoteItemDTO[] = [];

    // Helper function to check if an item matches the search query
    const itemMatches = (item: QuoteProductDTO) => {
      const name = item.name?.toLowerCase() || '';
      const sku = item.sku?.toLowerCase() || '';
      const vendor = item.vendor?.name?.toLowerCase() || '';
      return (
        name.includes(query) || sku.includes(query) || vendor.includes(query)
      );
    };

    (quoteItems || []).forEach(item => {
      if (item.type === 'product') {
        const product = item as QuoteProductDTO;

        // Check if product itself matches
        const productMatches = itemMatches(product);

        // Filter matching children
        const matchingChildren = product.children?.filter(child =>
          itemMatches(child as QuoteProductDTO)
        );

        // Include product if it matches OR if any children match
        if (
          productMatches ||
          (matchingChildren && matchingChildren.length > 0)
        ) {
          // If parent matches, show all children; if only children match, show only matching children
          filtered.push({
            ...item,
            children: productMatches ? product.children : matchingChildren
          });
        }
      } else if (item.type === 'section' && 'children' in item) {
        // Filter section products and their children
        const matchingProducts = (item.children as QuoteProductDTO[])
          ?.map((child: QuoteProductDTO) => {
            if (child.type !== 'product') return null;

            const product = child as QuoteProductDTO;
            const productMatches = itemMatches(product);

            // Filter matching children (options) of this product
            const matchingChildren = product.children?.filter(option =>
              itemMatches(option as QuoteProductDTO)
            );

            // Include product if it matches OR if any children match
            if (
              productMatches ||
              (matchingChildren && matchingChildren.length > 0)
            ) {
              // If parent matches, show all children; if only children match, show only matching children
              return {
                ...child,
                children: productMatches ? product.children : matchingChildren
              };
            }

            return null;
          })
          .filter(Boolean);

        // Include section only if it has matching products
        if (matchingProducts && matchingProducts.length > 0) {
          filtered.push({
            ...item,
            children: matchingProducts
          });
        }
      }
    });

    return filtered;
  }, [quoteItems, searchQuery]);

  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: filteredItems.length,
    getScrollElement: () => parentRef.current,
    getItemKey: index =>
      filteredItems[index]?.id || filteredItems[index]?._id || index,
    estimateSize: index => {
      const item = filteredItems[index];

      if (item.type === 'section' && 'children' in item) {
        const children = (item.children || []) as QuoteProductDTO[];

        // Empty section with "No Items Yet" empty state
        if (children.length === 0) {
          // 48 header + 24 sectionContainer padding + ~138 empty state
          return 220;
        }

        // Section with products
        const childrenGaps =
          children.length > 1 ? (children.length - 1) * 12 : 0;
        let childrenHeight = 0;
        for (let i = 0; i < children.length; i += 1) {
          const child = children[i];
          const hasNote = !!child.note?.trim();
          const baseHeight = hasNote ? 146 : 130;
          const options = child.children || [];
          let optionsHeight = 0;
          for (let j = 0; j < options.length; j += 1) {
            const opt = options[j] as QuoteProductDTO;
            const optNote = !!opt?.note?.trim();
            const optBadge = !!opt?.labelType;
            if (optNote) {
              optionsHeight += optBadge ? 148 : 140;
            } else {
              optionsHeight += optBadge ? 122 : 118;
            }
          }
          childrenHeight += baseHeight + optionsHeight;
        }
        // 60 header + 24 padding (top+bottom) + children + gaps
        return 60 + 24 + childrenHeight + childrenGaps;
      }

      // Standalone product
      const product = item as QuoteProductDTO;
      const hasNote = !!product.note?.trim();
      const baseHeight = hasNote ? 146 : 130;
      const options = product.children || [];
      let optionsHeight = 0;
      for (let i = 0; i < options.length; i += 1) {
        const opt = options[i] as QuoteProductDTO;
        const optNote = !!opt?.note?.trim();
        const optBadge = !!opt?.labelType;
        if (optNote) {
          optionsHeight += optBadge ? 148 : 140;
        } else {
          optionsHeight += optBadge ? 122 : 118;
        }
      }
      return baseHeight + optionsHeight;
    },
    gap: OUTER_GAP,
    overscan: 2
  });

  // Structural fingerprint that changes on any add/remove (item, subitem, SPO, section).
  // Includes grandchildren so subitems added to products inside sections are captured.
  const itemsFingerprint = useMemo(
    () =>
      filteredItems
        .map(item => {
          const id = item.id || item._id;
          const children = ('children' in item && item.children) || [];
          const childPart = children
            .map((c: any) => `${c.id || c._id}:${c.children?.length || 0}`)
            .join(';');
          return `${id}:${children.length}[${childPart}]`;
        })
        .join(','),
    [filteredItems]
  );

  // Reset cached measurements when structure changes so the virtualizer
  // recalculates positions using estimateSize, then measureElement re-measures
  // each visible element on the next paint.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(() => {
    virtualizer.measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsFingerprint, virtualizer]);

  // Enrich visible items with full data (images, SKU, etc.)
  const virtualItems = virtualizer.getVirtualItems();
  const visibleRootItems = useMemo(
    () => virtualItems.map(vr => filteredItems[vr.index]),
    [virtualItems, filteredItems]
  );
  useEnrichVisibleItems(quoteId, visibleRootItems, filteredItems);

  // Follow a collaborator: scroll to the requested quote item (set via the
  // presence stack). Resolves nested options / section children to their
  // top-level row. If the target is hidden by an active search, clear the
  // search first and let this effect re-run once the row is back in the list.
  useEffect(() => {
    if (!scrollToItemId) return;

    const index = findTopLevelIndex(filteredItems, scrollToItemId);
    if (index >= 0) {
      virtualizer.scrollToIndex(index, { align: 'center' });
      // Briefly outline the destination row (blue border) so the jump is
      // obvious — matches the RFQ card highlight. Highlight the EXACT id so a
      // followed option / section child pulses, not just its parent row.
      const targetId = scrollToItemId;
      setHighlightedItemId(targetId);
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = setTimeout(() => {
        if (useQuotePresenceStore.getState().highlightedItemId === targetId) {
          setHighlightedItemId(null);
        }
      }, 2200);
      requestScrollToItem(null);
      return;
    }

    if (searchQuery) {
      const params = new URLSearchParams(searchParams);
      params.delete('search');
      setSearchParams(params, { replace: true });
      return;
    }

    // Target isn't in this quote (stale request) — drop it.
    requestScrollToItem(null);
  }, [
    scrollToItemId,
    filteredItems,
    virtualizer,
    searchQuery,
    searchParams,
    setSearchParams,
    requestScrollToItem,
    setHighlightedItemId
  ]);

  // Clear any pending highlight timer on unmount.
  useEffect(
    () => () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    },
    []
  );

  return (
    <QuoteFieldLockProvider>
      <QuoteRowPresenceProvider>
        <div className={classes.container} ref={parentRef}>
          {filteredItems.length > 0 ? (
            <>
              <Box mb={2} className={classes.stickyHeader}>
                <ProductsTableHeader />
              </Box>

              <div
                key={itemsFingerprint}
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative'
                }}>
                {virtualItems.map(virtualRow => {
                  const item = filteredItems[virtualRow.index];
                  const itemId = (item.id || item._id) as string;

                  return (
                    <div
                      key={item.id || item._id}
                      ref={virtualizer.measureElement}
                      data-index={virtualRow.index}
                      data-quote-item-id={itemId}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        display: 'flow-root',
                        transform: `translateY(${virtualRow.start}px)`
                      }}>
                      {item.type === 'section' ? (
                        <SectionRow
                          section={item as any}
                          virtualRow={virtualRow}
                          scrollContainerRef={parentRef}
                          searchQuery={searchQuery}
                          onProductClick={onProductClick}
                          isRfqQuote={isRfqQuote}
                        />
                      ) : (
                        <ProductRow
                          product={item as QuoteProductDTO}
                          searchQuery={searchQuery}
                          onProductClick={onProductClick}
                          scrollContainerRef={parentRef}
                          productStartOffset={virtualRow.start}
                          isRfqQuote={isRfqQuote}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <Box
              style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
              <SEmptyState
                title="No Items Found"
                imageSrc="/images/no-my-snippets.png"
                subTitle="Please try again with different search query."
              />
            </Box>
          )}
        </div>
      </QuoteRowPresenceProvider>
    </QuoteFieldLockProvider>
  );
};
